/* ============================================================
   FavoritesPage — CB-6 (a90f4a83). One newest-first list over
   favorited shop products and performers. Deleted targets stay
   visible as muted rows (CB-5 «файл удалён» posture); products
   navigate to the product page; every row carries a remove
   action (same toggle endpoint as the hearts).
   ============================================================ */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type { FavoriteItem } from '../types/favorite';
import {
  addedAtLabel,
  buildFavoritesPageView,
  type FavoriteRowView,
} from './favoritesView';

interface Props {
  onOpenProduct: (id: string) => void;
  onNeedAuth: () => void;
}

const badgeToneStyle: Record<string, { background: string; color: string }> = {
  accent: { background: 'rgba(255,149,0,0.08)', color: 'var(--color-primary)' },
  danger: { background: 'rgba(255,69,58,0.08)', color: 'var(--color-danger)' },
  muted: { background: 'var(--color-surface-secondary, #F2F2F7)', color: 'var(--color-text-secondary)' },
};

export function FavoritesPage({ onOpenProduct, onNeedAuth }: Props) {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState<FavoriteItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await api.getFavorites();
      setItems(list.items ?? []);
      setTotalCount(list.totalCount ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      onNeedAuth();
      return;
    }
    void load();
  }, [isAuthenticated, load, onNeedAuth]);

  const remove = async (item: FavoriteItem) => {
    setBusyId(item.id);
    setError(null);
    setNotice(null);
    try {
      const result = item.kind === 'product'
        ? await api.toggleProductFavorite(item.productId)
        : await api.toggleProviderFavorite(item.providerUserId);
      if (!result.favorited) {
        setNotice(item.kind === 'product' ? 'Товар убран из избранного' : 'Исполнитель убран из избранного');
        setItems((prev) => prev.filter((f) => f.id !== item.id));
        setTotalCount((prev) => Math.max(0, prev - 1));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось обновить избранное');
    } finally {
      setBusyId(null);
    }
  };

  const view = buildFavoritesPageView({ items, totalCount });

  const row = (r: FavoriteRowView, item: FavoriteItem) => {
    const added = addedAtLabel(item.addedAtUtc);
    const inner = (
      <>
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 'var(--radius-md)',
            background: r.muted ? 'var(--color-surface-secondary, #F2F2F7)' : 'rgba(255,149,0,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 'var(--fs-h3)',
            flexShrink: 0,
          }}
        >
          {r.glyph}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-sm)',
              alignItems: 'center',
              flexWrap: 'wrap',
              opacity: r.muted ? 0.6 : 1,
            }}
          >
            <span
              style={{
                fontSize: 'var(--fs-caption)',
                color: 'var(--color-text-tertiary)',
                background: 'none',
              }}
            >
              {r.kindLabel}
            </span>
            <span style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-primary)' }}>
              {r.title}
            </span>
            {r.badge && (
              <span
                style={{
                  fontSize: 'var(--fs-caption)',
                  padding: '2px 8px',
                  borderRadius: 'var(--radius-sm)',
                  fontWeight: 600,
                  ...badgeToneStyle[r.badge.tone],
                }}
              >
                {r.badge.text}
              </span>
            )}
          </div>
          <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', marginTop: '2px' }}>
            {r.subtitle}
            {added ? ` · ${added}` : ''}
          </div>
        </div>
        {r.priceText && (
          <span style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-bold)', color: 'var(--color-primary)' }}>
            {r.priceText}
          </span>
        )}
        <button
          className="btn btn-secondary"
          onClick={(e) => {
            e.stopPropagation();
            void remove(item);
          }}
          disabled={busyId === item.id}
          title={r.removeLabel}
          style={{ flexShrink: 0 }}
        >
          {busyId === item.id ? '…' : '♡'}
        </button>
      </>
    );

    if (r.clickable && r.productId) {
      return (
        <button
          key={r.key}
          onClick={() => onOpenProduct(r.productId!)}
          className="card"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-md)',
            padding: 'var(--space-md)',
            textAlign: 'left',
            cursor: 'pointer',
          }}
        >
          {inner}
        </button>
      );
    }

    return (
      <div key={r.key} className="card" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', padding: 'var(--space-md)' }}>
        {inner}
      </div>
    );
  };

  return (
    <div className="container" style={{ paddingTop: 'var(--space-xl)', paddingBottom: 'var(--space-2xl)' }}>
      <div style={{ marginBottom: 'var(--space-lg)' }}>
        <h1 style={{ fontSize: 'var(--fs-h1)', fontWeight: 'var(--fw-bold)', letterSpacing: '-0.02em' }}>
          {view.headline}
        </h1>
        <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
          Товары и исполнители, которые вы сохранили
        </p>
      </div>

      {notice && (
        <div className="card" style={{ padding: 'var(--space-sm) var(--space-md)', marginBottom: 'var(--space-md)', color: 'var(--color-primary)' }}>
          {notice}
        </div>
      )}

      {loading ? (
        <div style={{ display: 'grid', gap: 'var(--space-md)' }}>
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton" style={{ height: 80, borderRadius: 'var(--radius-lg)' }} />
          ))}
        </div>
      ) : error ? (
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ color: 'var(--color-danger)' }}>{error}</p>
          <button className="btn btn-secondary" onClick={() => void load()} style={{ marginTop: 'var(--space-md)' }}>
            Повторить
          </button>
        </div>
      ) : view.rows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
          <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>{view.emptyTitle}</p>
          <p style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>{view.emptyHint}</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-md)' }}>
          {view.rows.map((r, i) => row(r, items[i]))}
        </div>
      )}
    </div>
  );
}
